import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import StepIndicator from './components/StepIndicator';
import ProfileSetupForm from './components/ProfileSetupForm';
import PatientIntroduction from './components/PatientIntroduction';
import ScheduleIntroduction from './components/ScheduleIntroduction';
import { useAuth } from '../../contexts/AuthContext';
import { stripeService } from '../../services/stripeService';
import ErrorDialog from './components/ErrorDialog';
import { supabase } from '../../lib/supabase';

const OnboardingFlow = () => {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const { completeOnboarding, userProfile, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    clinicName: '',
    specializations: [],
    crp: '',
    phone: '',
    whatsappNumber: '',
    address: '',
    clinicLogo: null,
    professionalPhoto: null,
  });

  const steps = [
    {
      id: 'profile',
      title: 'Perfil',
      description: 'Seus dados profissionais',
      icon: 'User',
    },
    {
      id: 'patients',
      title: 'Pacientes',
      description: 'Conheça o sistema',
      icon: 'Users',
    },
    {
      id: 'schedule',
      title: 'Agenda',
      description: 'Configure horários',
      icon: 'Calendar',
    },
  ];

  useEffect(() => {
    const savedProgress = localStorage.getItem('onboardingProgress');
    if (savedProgress) {
      const { step, completed, data } = JSON.parse(savedProgress);
      setCurrentStep(step);
      setCompletedSteps(completed);
      setFormData((prev) => ({ ...prev, ...data }));
    }
  }, []);

  useEffect(() => {
    const progress = {
      step: currentStep,
      completed: completedSteps,
      data: formData,
    };
    localStorage.setItem('onboardingProgress', JSON.stringify(progress));
  }, [currentStep, completedSteps, formData]);

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const saveProfileData = async () => {
    if (!userProfile?.id) {
      console.error('Nenhum perfil de psicólogo encontrado');
      return { error: 'Nenhum perfil de psicólogo encontrado' };
    }

    try {
      let professionalPhotoUrl = null;
      let clinicLogoUrl = null;

      // 1. Upload da foto profissional
      if (formData.professionalPhoto instanceof File) {
        const file = formData.professionalPhoto;
        const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
        const timestamp = Date.now();
        const fileName = `${userProfile.id}-profile-${timestamp}.${ext}`;
        const filePath = `professionals/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(filePath, file, {
            upsert: false,
            contentType: file.type || 'image/jpeg',
            cacheControl: '3600'
          });

        if (uploadError) {
          console.error('Erro ao subir foto profissional:', uploadError);
          throw uploadError;
        }

        const { data: urlData } = supabase.storage
          .from('profile-photos')
          .getPublicUrl(filePath);

        professionalPhotoUrl = urlData.publicUrl;
      }

      // 2. Upload do logo da clínica (opcional – só se existir)
      if (formData.clinicLogo instanceof File) {
        const file = formData.clinicLogo;
        const ext = file.name.split('.').pop()?.toLowerCase() || 'png';
        const timestamp = Date.now();
        const fileName = `${userProfile.id}-clinic-${timestamp}.${ext}`;
        const filePath = `clinics/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('profile-photos')
          .upload(filePath, file, {
            upsert: false,
            contentType: file.type || 'image/png'
          });

        if (uploadError) throw uploadError;

        const { data: urlData } = supabase.storage
          .from('profile-photos')
          .getPublicUrl(filePath);

        clinicLogoUrl = urlData.publicUrl;
      }

      // 3. Atualiza o perfil com as URLs (e os outros dados)
      const updates = {
        full_name: formData?.fullName,
        display_name: formData?.displayName,
        clinic_name: formData?.clinicName,
        specialization: formData.specializations || [],
        crp: formData?.crp,
        phone: formData?.phone || null,
        whatsapp_number: formData?.whatsappNumber,
        clinic_address: formData?.address || null,
        
        // ← Campos importantes que estavam faltando
        profile_photo_url: professionalPhotoUrl,
        clinic_logo_url: clinicLogoUrl,
        
        updated_at: new Date().toISOString()
      };

      const { psychologistService } = await import('../../services/psychologistService');
      const { data, error } = await psychologistService.updatePsychologist(
        userProfile.id,
        updates
      );

      if (error) throw error;

      // Opcional: limpar as fotos do formData após upload bem-sucedido
      setFormData(prev => ({
        ...prev,
        professionalPhoto: null,
        clinicLogo: null
      }));

      return { data, error: null };

    } catch (err) {
      console.error('Erro completo ao salvar perfil:', err);
      return { error: err.message || 'Erro desconhecido ao salvar perfil' };
    }
  };

  const handleNextStep = async () => {
    // Save profile data when moving from step 0 (Profile)
    if (currentStep === 0) {
      setIsSubmitting(true);
      const { error } = await saveProfileData();
      setIsSubmitting(false);

      if (error) {
        if (error?.code === '23505'|| error.message?.includes('psychologists_crp_key')) {
          setError('Já existe um psicólogo cadastrado com esse CRP');
          setShowErrorDialog(true);
          return;
        }
        else            
        {
          setError('Erro ao salvar dados do perfil. Por favor, tente novamente.');
          return;
        }
      }
    }

    if (!completedSteps?.includes(currentStep)) {
      setCompletedSteps((prev) => [...prev, currentStep]);
    }

    if (currentStep < steps?.length - 1) {
      setCurrentStep(currentStep + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSkipStep = () => {
    handleNextStep();
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    
    try {
      // Mark onboarding as complete
      const { error: onboardingError } = await completeOnboarding();
      
      if (onboardingError) {
        console.error('Error completing onboarding:', onboardingError);
        // Continue anyway - user can still use the app
      }

      setTimeout(async () => {

        handleTrialSubscription();

      }, 1000);
    } catch (error) {
      console.error('Onboarding completion error:', error);
      // Continue anyway - user can still use the app
      setTimeout(async () => {

        handleTrialSubscription();

      }, 1000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTrialSubscription = async () => {
    // Cria o trial automático de 15 dias SEM cartão.

    // Define o tipo de assinatura inicial (default) como o básico.
    const plan = "basic";

    // Chama a criação da assinatura trial, sem cartão.
    await stripeService.createSubscription(
      userProfile.id,
      plan,
      null                     // ← null = trial sem cartão
    );

    // Mostra a página principal (agenda).
    navigate('/schedule-dashboard');    
  }


  const handleExit = () => {
    const confirmExit = window.confirm(
      'Tem certeza que deseja sair? Seu progresso será salvo.'
    );
    if (confirmExit) {
      navigate('/authentication-hub');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#c3c3c3' }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-blue-700 flex items-center justify-center shadow-elevated">
              <Icon name="Activity" size={24} color="#FFFFFF" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold text-foreground">
                {import.meta.env.VITE_APP_TITLE}
              </h1>
              <p className="text-sm text-muted-foreground">
                Configuração Inicial
              </p>
            </div>
          </div>
          <button
            onClick={handleExit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-all duration-200"
          >
            <Icon name="X" size={16} />
            <span className="hidden sm:inline">Sair</span>
          </button>
        </div>

        <div className="bg-card rounded-2xl shadow-elevated border border-border p-6 sm:p-8">
          <StepIndicator steps={steps} currentStep={currentStep} />

          <div className="mt-8">
            {currentStep === 0 && (
              <ProfileSetupForm
                formData={formData}
                setFormData={setFormData}
                onChange={handleInputChange}
                onNext={handleNextStep}
                onSkip={handleSkipStep}
              />
            )}

            {currentStep === 1 && (
              <PatientIntroduction
                onNext={handleNextStep}
                onBack={handlePreviousStep}
                onSkip={handleSkipStep}
              />
            )}

            {currentStep === 2 && (
              <ScheduleIntroduction
                onComplete={handleComplete}
                onBack={handlePreviousStep}
                onSkip={() => handleComplete({})}
              />
            )}
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Etapa {currentStep + 1} de {steps?.length} •{' '}
            {Math.round(((currentStep + 1) / steps?.length) * 100)}% concluído
          </p>
        </div>
      </div>

      {error && showErrorDialog && (
      <ErrorDialog
        isOpen={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        message={error}
      />
      )}

    </div>

    
  );
};

export default OnboardingFlow;